import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  Link,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  CheckIcon,
  CloseIcon,
  ExternalLinkIcon,
  LockIcon,
} from '@chakra-ui/icons';
import {
  apiGetBaraReferral,
  apiGetBaraComments,
  apiAddBaraComment,
  apiApproveBaraReferral,
  apiDeclineBaraReferral,
  apiRequestBaraInfo,
  apiResumeBaraReview,
} from 'api';
import { AuthContext } from 'contexts/AuthContext';
import { useAppSelector } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import {
  JIRA_BASE_URL,
  ROLE_BROKER,
  ROLE_BROKER_PARTNER,
  BARA_REFERRAL_STATUS_READY_FOR_REVIEW,
  BARA_REFERRAL_STATUS_UNDER_REVIEW,
  BARA_REFERRAL_STATUS_APPROVED,
  BARA_REFERRAL_STATUS_DECLINED,
  BARA_REFERRAL_STATUS_RECALLED,
  BARA_REFERRAL_STATUS_NEEDS_INFO,
} from 'variables/constants';
import type { APIBaraReferral, APIBaraComment } from 'types/api.types';
import { useBaraFiles } from 'views/bara/hooks/useBaraFiles';
import SupportingFilesSection from 'views/shared/ReferToUnderwriting/components/SupportingFilesSection';

const STATUS_BADGE: Record<number, { label: string; color: string }> = {
  [BARA_REFERRAL_STATUS_READY_FOR_REVIEW]: {
    label: 'Ready for Review',
    color: 'orange',
  },
  [BARA_REFERRAL_STATUS_UNDER_REVIEW]: { label: 'Under Review', color: 'blue' },
  [BARA_REFERRAL_STATUS_APPROVED]: { label: 'Approved', color: 'green' },
  [BARA_REFERRAL_STATUS_DECLINED]: { label: 'Declined', color: 'red' },
  [BARA_REFERRAL_STATUS_RECALLED]: { label: 'Recalled', color: 'gray' },
  [BARA_REFERRAL_STATUS_NEEDS_INFO]: { label: 'Needs Info', color: 'yellow' },
};

// ── Decision confirmation modal ────────────────────────────────────────────────
interface DecisionPanelProps {
  action: 'approve' | 'decline';
  referralId: string;
  onSuccess: () => void;
  onBack: () => void;
}

const DecisionPanel = ({
  action,
  referralId,
  onSuccess,
  onBack,
}: DecisionPanelProps) => {
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);
  const isApprove = action === 'approve';
  const isDisabled = justification.trim().length < 10;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (isApprove) {
        await apiApproveBaraReferral(referralId, justification.trim());
      } else {
        await apiDeclineBaraReferral(referralId, justification.trim());
      }
      toastRef.current({
        title: isApprove ? 'Referral approved' : 'Referral declined',
        status: isApprove ? 'success' : 'error',
        duration: 3000,
      });
      onSuccess();
    } catch {
      toastRef.current({
        title: 'Failed to submit decision',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={8} maxW="520px" mx="auto">
      <Flex align="center" gap={4} mb={4}>
        <Flex
          align="center"
          justify="center"
          w={12}
          h={12}
          borderRadius="xl"
          flexShrink={0}
          bg={isApprove ? 'green.50' : 'red.50'}
          border="1px solid"
          borderColor={isApprove ? 'green.200' : 'red.200'}
        >
          <Icon
            as={isApprove ? CheckIcon : CloseIcon}
            color={isApprove ? 'green.600' : 'red.600'}
            boxSize={5}
          />
        </Flex>
        <Heading size="md">
          {isApprove ? 'Approve referral' : 'Decline referral'}
        </Heading>
      </Flex>

      <Text fontSize="sm" color="gray.500" mb={5}>
        {isApprove
          ? 'On confirm, the submission proceeds through the normal broker workflow and Jira is updated to Approved.'
          : 'On confirm, this submission is terminated — the broker must create a new submission. Jira is updated to Declined.'}
      </Text>

      <Box mb={2}>
        <Text fontSize="xs" fontWeight="600" mb={1}>
          Justification{' '}
          <Text as="span" color="red.500">
            *
          </Text>
          <Text as="span" color="gray.400" fontWeight="400" ml={2}>
            required · written to Jira
          </Text>
        </Text>
        <Textarea
          placeholder={
            isApprove
              ? 'Provide your approval rationale…'
              : 'Provide your reason for declining…'
          }
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          rows={4}
          fontSize="sm"
          borderColor={isApprove ? 'green.200' : 'red.200'}
          _focus={{
            borderColor: isApprove ? 'green.400' : 'red.400',
            boxShadow: 'none',
          }}
        />
      </Box>

      {isDisabled && (
        <HStack spacing={1} mb={4}>
          <Icon as={LockIcon} color="gray.400" boxSize={3} />
          <Text fontSize="xs" color="gray.400">
            Confirm {isApprove ? 'Approval' : 'Decline'} stays disabled until a
            justification is entered.
          </Text>
        </HStack>
      )}

      <HStack justify="space-between" mt={4}>
        <Button variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button
          size="sm"
          colorScheme={isApprove ? 'green' : 'red'}
          isDisabled={isDisabled}
          isLoading={saving}
          onClick={handleConfirm}
        >
          {isApprove ? 'Confirm Approval' : 'Confirm Decline'}
        </Button>
      </HStack>
    </Box>
  );
};

// ── Main modal ─────────────────────────────────────────────────────────────────
interface Props {
  referralId: string;
  isOpen: boolean;
  onClose: () => void;
  onDecision?: () => void;
}

const ReferralReviewModal = ({
  referralId,
  isOpen,
  onClose,
  onDecision,
}: Props) => {
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const { user } = useContext(AuthContext) as {
    user: { email: string; role: number };
  };

  const [referral, setReferral] = useState<APIBaraReferral | null>(null);
  const [comments, setComments] = useState<APIBaraComment[]>([]);

  const activityEvents = comments.filter(
    (c) =>
      c.comment_type === 'request_info' ||
      c.comment_type === 'approved' ||
      c.comment_type === 'declined',
  );
  const chatComments = comments.filter((c) => c.comment_type === 'comment');
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const {
    baraFiles,
    isUploadingFile,
    handleFilesUpload,
    handleFileRemove,
    deletingFileId,
  } = useBaraFiles(
    referral?.policy_uuid ?? '',
    isOpen,
    referral?.supporting_files,
  );
  const [decisionAction, setDecisionAction] = useState<
    'approve' | 'decline' | null
  >(null);
  const [actioning, setActioning] = useState(false);
  const [requestInfoText, setRequestInfoText] = useState('');
  const [showRequestInfo, setShowRequestInfo] = useState(false);

  const load = useCallback(async () => {
    if (!referralId) return;
    setLoading(true);
    try {
      const [referralData, commentsData] = await Promise.all([
        apiGetBaraReferral(referralId),
        apiGetBaraComments(referralId),
      ]);
      setReferral(referralData as APIBaraReferral);
      setComments(commentsData as APIBaraComment[]);
    } catch {
      toastRef.current({
        title: 'Failed to load referral',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [referralId]);

  useEffect(() => {
    if (isOpen && referralId) {
      setDecisionAction(null);
      setCommentText('');
      setRequestInfoText('');
      setShowRequestInfo(false);
      load();
    }
  }, [isOpen, referralId, load]);

  const handleSendComment = async () => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      await apiAddBaraComment(referralId, commentText.trim());
      setCommentText('');
      toastRef.current({
        title: 'Comment sent',
        status: 'success',
        duration: 2000,
      });
      const updated = await apiGetBaraComments(referralId);
      setComments(updated as APIBaraComment[]);
    } catch {
      toastRef.current({
        title: 'Failed to send comment',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSendingComment(false);
    }
  };

  const handleDecisionSuccess = () => {
    onClose();
    onDecision?.();
  };

  const handleRequestInfo = async () => {
    if (!requestInfoText.trim()) return;
    setActioning(true);
    try {
      await apiRequestBaraInfo(referralId, { message: requestInfoText.trim() });
      toastRef.current({
        title: 'Information request sent to broker',
        status: 'success',
        duration: 3000,
      });
      setRequestInfoText('');
      setShowRequestInfo(false);
      await load();
    } catch {
      toastRef.current({
        title: 'Failed to send request',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setActioning(false);
    }
  };

  const handleResumeReview = async () => {
    setActioning(true);
    try {
      await apiResumeBaraReview(referralId);
      toastRef.current({
        title: 'Review resumed',
        status: 'success',
        duration: 2000,
      });
      await load();
    } catch {
      toastRef.current({
        title: 'Failed to resume review',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setActioning(false);
    }
  };

  const badge = referral ? STATUS_BADGE[referral.status] : null;
  const isNeedsInfo = referral?.status === BARA_REFERRAL_STATUS_NEEDS_INFO;
  const baraCapabilities = useAppSelector((state) => state.bara.capabilities);
  const isBaraFixesEnabled = useFeatureFlag('enable-bara-referral-fixes');
  // A broker/partner who also has BARA authority (e.g. delegate) is a reviewer, not a broker view
  const isBrokerView =
    (user?.role === ROLE_BROKER || user?.role === ROLE_BROKER_PARTNER) &&
    !baraCapabilities?.can_act_as_bara_authority;
  const isAssignedToMe = referral?.is_assigned_to_me ?? false;
  // Broker who submitted this specific referral
  const isSubmitter = referral?.submitted_by === user?.email;
  // The backend checks assignment, status, and active BARA authority.
  const isActionable = referral?.can_decide ?? false;
  const lockEdits =
    isBaraFixesEnabled &&
    (referral?.status === BARA_REFERRAL_STATUS_APPROVED ||
      referral?.status === BARA_REFERRAL_STATUS_DECLINED);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        maxW="1200px"
        mx="auto"
        my={6}
        borderRadius="xl"
        overflow="hidden"
      >
        {loading || !referral ? (
          <Flex justify="center" align="center" py={16} gap={3}>
            <Spinner size="sm" />
            <Text color="gray.500">Loading referral…</Text>
          </Flex>
        ) : decisionAction ? (
          <DecisionPanel
            action={decisionAction}
            referralId={referralId}
            onSuccess={handleDecisionSuccess}
            onBack={() => setDecisionAction(null)}
          />
        ) : (
          <Flex direction="column" h="85vh">
            {/* ── Header ── */}
            <Box px={6} py={4} borderBottom="1px" borderColor="gray.100">
              <Text
                fontSize="xs"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={1}
              >
                MP/Delegate Review
              </Text>
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Heading size="md">
                    {referral.submission_id
                      ? `Submission #${referral.submission_id}`
                      : `Referral #${referralId.slice(0, 8).toUpperCase()}`}
                  </Heading>
                  {badge && (
                    <Badge
                      colorScheme={badge.color}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {badge.label}
                      {isAssignedToMe ? ' · assigned to you' : ''}
                    </Badge>
                  )}
                </HStack>
                <HStack spacing={4}>
                  {referral.jira_ticket_key && (
                    <Link
                      href={`${JIRA_BASE_URL}/browse/${referral.jira_ticket_key}`}
                      isExternal
                      fontSize="sm"
                      color="blue.500"
                    >
                      {referral.jira_ticket_key}{' '}
                      <Icon as={ExternalLinkIcon} boxSize={3} />
                    </Link>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    color="gray.500"
                    onClick={onClose}
                  >
                    ✕
                  </Button>
                </HStack>
              </Flex>
            </Box>

            {/* ── Body: split pane ── */}
            <Grid templateColumns="1fr 380px" flex={1} overflow="hidden">
              {/* Left: Submission */}
              <GridItem
                overflow="auto"
                borderRight="1px"
                borderColor="gray.100"
                p={6}
              >
                <HStack justify="space-between" mb={5}>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.500"
                  >
                    Submission
                  </Text>
                  {referral.submission_id && referral.policy_uuid && (
                    <Link
                      href={`/admin/policies/submission/${referral.submission_id}/${referral.policy_uuid}/personal`}
                      isExternal
                      fontSize="sm"
                      color="blue.500"
                      fontWeight="medium"
                    >
                      View full submission{' '}
                      <Icon as={ExternalLinkIcon} boxSize={3} />
                    </Link>
                  )}
                </HStack>

                <Box bg="gray.50" borderRadius="lg" p={5} mb={5}>
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.400"
                    mb={4}
                  >
                    Policy Information
                  </Text>
                  <Grid templateColumns="1fr 1fr 1fr" gap={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.400" mb={1}>
                        Effective Date
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {referral.effective_date || '—'}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400" mb={1}>
                        Market
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {referral.market || '—'}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400" mb={1}>
                        Broker
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {referral.submitted_by}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400" mb={1}>
                        Submitted
                      </Text>
                      <Text fontSize="sm">
                        {new Date(referral.submitted_at).toLocaleString()}
                      </Text>
                    </Box>
                    {referral.assigned_to_name && (
                      <Box>
                        <Text fontSize="xs" color="gray.400" mb={1}>
                          Assigned To
                        </Text>
                        <Text fontSize="sm">{referral.assigned_to_name}</Text>
                      </Box>
                    )}
                    {referral.decided_at && (
                      <Box>
                        <Text fontSize="xs" color="gray.400" mb={1}>
                          Decision Date
                        </Text>
                        <Text fontSize="sm">
                          {new Date(referral.decided_at).toLocaleString()}
                        </Text>
                      </Box>
                    )}
                  </Grid>
                </Box>

                {referral.validations_snapshot?.length > 0 && (
                  <Box
                    bg="orange.50"
                    border="1px"
                    borderColor="orange.200"
                    borderRadius="lg"
                    p={5}
                    mb={5}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="orange.500"
                      mb={2}
                    >
                      Blocking Validations
                    </Text>
                    {referral.validations_snapshot.map((v, i) => (
                      <Text key={i} fontSize="sm" color="orange.800">
                        • {typeof v === 'object' ? v.message : v}
                      </Text>
                    ))}
                  </Box>
                )}

                {referral.justification && (
                  <Box bg="gray.50" borderRadius="lg" p={5} mb={5}>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="gray.400"
                      mb={2}
                    >
                      Reason for Referral
                    </Text>
                    <Text fontSize="sm">{referral.justification}</Text>
                  </Box>
                )}

                {/* Decision trail — Request Info, Approved, Declined events */}
                {activityEvents.length > 0 && (
                  <Box mb={5}>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="gray.400"
                      mb={3}
                    >
                      Activity
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {activityEvents.map((ev) => {
                        const borderColor =
                          ev.comment_type === 'request_info'
                            ? 'orange.300'
                            : ev.comment_type === 'approved'
                              ? 'green.400'
                              : 'red.400';
                        const bgColor =
                          ev.comment_type === 'request_info'
                            ? 'orange.50'
                            : ev.comment_type === 'approved'
                              ? 'green.50'
                              : 'red.50';
                        const labelColor =
                          ev.comment_type === 'request_info'
                            ? 'orange.600'
                            : ev.comment_type === 'approved'
                              ? 'green.700'
                              : 'red.700';
                        const label =
                          ev.comment_type === 'request_info'
                            ? 'Information Requested'
                            : ev.comment_type === 'approved'
                              ? 'Approved'
                              : 'Declined';
                        return (
                          <Box
                            key={ev.id}
                            bg={bgColor}
                            borderRadius="md"
                            p={3}
                            borderLeft="3px solid"
                            borderLeftColor={borderColor}
                          >
                            <HStack justify="space-between" mb={1}>
                              <HStack spacing={2}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color="gray.700"
                                >
                                  {ev.author}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color={labelColor}
                                >
                                  · {label}
                                </Text>
                              </HStack>
                              <Text fontSize="xs" color="gray.400">
                                {new Date(ev.created).toLocaleString()}
                              </Text>
                            </HStack>
                            {ev.body && (
                              <Text
                                fontSize="sm"
                                color="gray.700"
                                whiteSpace="pre-wrap"
                              >
                                {ev.body}
                              </Text>
                            )}
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                {/* SIO flag + post-bind review notes — shown when set */}
                {(referral.is_sio ||
                  referral.post_bind_review_notes ||
                  referral.post_bind_reviewed_at) && (
                  <Box
                    bg="orange.50"
                    border="1px"
                    borderColor="orange.200"
                    borderRadius="lg"
                    p={5}
                    mb={5}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="orange.600"
                      mb={3}
                    >
                      Post-Bind Review
                    </Text>
                    <HStack mb={2}>
                      <Badge
                        colorScheme={referral.is_sio ? 'orange' : 'gray'}
                        fontSize="10px"
                      >
                        SIO: {referral.is_sio ? 'Flagged' : 'Not flagged'}
                      </Badge>
                      {referral.post_bind_reviewed_by && (
                        <Text fontSize="xs" color="gray.500">
                          Reviewed by {referral.post_bind_reviewed_by}
                        </Text>
                      )}
                    </HStack>
                    {referral.post_bind_review_notes && (
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        whiteSpace="pre-wrap"
                      >
                        {referral.post_bind_review_notes}
                      </Text>
                    )}
                  </Box>
                )}

                {/* Request Info inline form — shown when button is clicked */}
                {!isBrokerView && showRequestInfo && (
                  <Box
                    mt={4}
                    bg="orange.50"
                    border="1px"
                    borderColor="orange.200"
                    borderRadius="lg"
                    p={4}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color="orange.700"
                      mb={2}
                    >
                      Request More Information
                    </Text>
                    <Textarea
                      placeholder="Describe what information is needed from the broker…"
                      value={requestInfoText}
                      onChange={(e) => setRequestInfoText(e.target.value)}
                      rows={3}
                      fontSize="sm"
                      bg="white"
                      mb={3}
                    />
                    <HStack>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => setShowRequestInfo(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        isDisabled={!requestInfoText.trim()}
                        isLoading={actioning}
                        onClick={handleRequestInfo}
                      >
                        Send Request
                      </Button>
                    </HStack>
                  </Box>
                )}
              </GridItem>

              <GridItem
                overflow="auto"
                bg="gray.50"
                display="flex"
                flexDirection="column"
              >
                <Flex
                  px={5}
                  pt={5}
                  pb={3}
                  justify="space-between"
                  align="center"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color="gray.500"
                  >
                    Communication
                  </Text>
                  {referral.jira_ticket_key && (
                    <HStack spacing={1}>
                      <Box w={2} h={2} borderRadius="full" bg="green.400" />
                      <Text fontSize="xs" color="gray.500">
                        Synced with Jira
                      </Text>
                    </HStack>
                  )}
                </Flex>

                <Tabs
                  size="sm"
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  px={5}
                >
                  <TabList mb={3}>
                    <Tab>Comments ({chatComments.length})</Tab>
                    <Tab>Attachments</Tab>
                  </TabList>
                  <TabPanels flex={1} overflow="auto">
                    <TabPanel px={0} pb={0}>
                      <VStack align="stretch" spacing={3} flex={1}>
                        {chatComments.length === 0 ? (
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            py={4}
                            textAlign="center"
                          >
                            No comments yet.
                          </Text>
                        ) : (
                          chatComments.map((c) => (
                            <Box
                              key={c.id}
                              bg="white"
                              borderRadius="md"
                              p={3}
                              shadow="sm"
                            >
                              <HStack justify="space-between" mb={1}>
                                <Text fontSize="xs" fontWeight="semibold">
                                  {c.author}
                                </Text>
                                <Text fontSize="xs" color="gray.400">
                                  {new Date(c.created).toLocaleString()}
                                </Text>
                              </HStack>
                              <Text fontSize="sm" whiteSpace="pre-wrap">
                                {c.body}
                              </Text>
                            </Box>
                          ))
                        )}
                      </VStack>
                    </TabPanel>
                    <TabPanel px={0}>
                      {!isSubmitter && baraFiles.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" mt={2}>
                          No attachments uploaded by the broker.
                        </Text>
                      ) : (
                        <SupportingFilesSection
                          uploadedFiles={baraFiles}
                          onFilesUpload={
                            isSubmitter ? handleFilesUpload : undefined
                          }
                          onFileRemove={
                            isSubmitter ? handleFileRemove : undefined
                          }
                          isUploadingFile={isUploadingFile}
                          isDeletingFileId={deletingFileId}
                          readOnly={lockEdits || !isSubmitter}
                        />
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>

                {/* Reply box — submitting broker and MP/Delegate only */}
                {!lockEdits && (isSubmitter || !isBrokerView) && (
                  <Box
                    px={5}
                    pb={5}
                    pt={3}
                    borderTop="1px"
                    borderColor="gray.200"
                    bg="gray.50"
                  >
                    <Textarea
                      placeholder="Reply to broker…"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                      fontSize="sm"
                      bg="white"
                      mb={2}
                    />
                    <Flex justify="flex-end">
                      <Button
                        size="sm"
                        colorScheme="brand"
                        isDisabled={!commentText.trim()}
                        isLoading={sendingComment}
                        onClick={handleSendComment}
                      >
                        Reply
                      </Button>
                    </Flex>
                  </Box>
                )}
              </GridItem>
            </Grid>

            {/* ── Footer ── */}
            <Box
              borderTop="1px"
              borderColor="gray.100"
              px={6}
              py={3}
              bg="white"
            >
              <Flex justify="space-between" align="center">
                <HStack spacing={1} color="gray.400">
                  <Icon as={LockIcon} boxSize={3} />
                  <Text fontSize="xs">
                    {isAssignedToMe
                      ? 'Assigned to you — cannot be reassigned once under review'
                      : referral.assigned_to_name
                        ? `Assigned to ${referral.assigned_to_name}`
                        : 'Unassigned'}
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <Button
                    variant="ghost"
                    size="sm"
                    color="gray.500"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  {isAssignedToMe && isNeedsInfo && (
                    <Button
                      size="sm"
                      variant="outline"
                      isLoading={actioning}
                      onClick={handleResumeReview}
                    >
                      Resume Review
                    </Button>
                  )}
                  {!isBrokerView && isActionable && !showRequestInfo && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="orange"
                      onClick={() => setShowRequestInfo(true)}
                    >
                      Request Info
                    </Button>
                  )}
                  {!isBrokerView && isActionable && (
                    <>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        leftIcon={<CloseIcon boxSize={2.5} />}
                        onClick={() => setDecisionAction('decline')}
                      >
                        Decline
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={<CheckIcon boxSize={2.5} />}
                        onClick={() => setDecisionAction('approve')}
                      >
                        Approve
                      </Button>
                    </>
                  )}
                </HStack>
              </Flex>
            </Box>
          </Flex>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReferralReviewModal;
